<template>
  <section class="landing-match-showcase">
    <div class="showcase-shell">
      <CardRailSkeleton v-if="isLoading" />
      <div class="showcase-grid" v-if="displayGames.length">
        <article
          v-for="game in displayGames"
          :key="game.id"
          class="showcase-card"
          :class="{ 'showcase-card-primary': isPrimaryGame(game), 'showcase-card-secondary': !isPrimaryGame(game) }"
          @click="openGame(game)"
        >
          <div class="card-top">
            <span class="league-pill">{{ game.league }}</span>
            <span class="countdown-pill">{{ countdownFor(game.date) }}</span>
          </div>

          <div class="match-row">
            <div class="team-block">
              <TeamLogoBadge :src="game.first_team_logo" :team-name="game.first_team" :size="56" />
              <span>{{ game.first_team }}</span>
            </div>

            <div class="versus-block">
              <span class="versus">vs</span>
              <span class="kickoff">{{ kickoffLabel(game.date) }}</span>
            </div>

            <div class="team-block">
              <TeamLogoBadge :src="game.second_team_logo" :team-name="game.second_team" :size="56" />
              <span>{{ game.second_team }}</span>
            </div>
          </div>

          <div class="prediction-panel">
            <div class="prediction-metric">
              <span class="metric-label">Pick</span>
              <span class="metric-value">{{ game.pro_category }}</span>
            </div>
            <div class="prediction-metric">
              <span class="metric-label">Odds</span>
              <span class="metric-value">{{ formattedOdds(game.pro_odds) }}</span>
            </div>
            <div class="prediction-metric">
              <span class="metric-label">Prob.</span>
              <span class="metric-value">{{ formattedProbability(game.pro_prob) }}</span>
            </div>
          </div>
        </article>
      </div>

      <div class="showcase-actions">
        <button class="football-cta" @click="goToFootball">
          Explore All Football Predictions
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import CardRailSkeleton from "./CardRailSkeleton.vue";
import TeamLogoBadge from "./TeamLogoBadge.vue";
import { formatLeagueName } from "../utils/formatLeagueName";
import { fetchJsonWithCache } from "../services/apiCache";

export default {
  name: "LandingMatchShowcase",
  components: {
    CardRailSkeleton,
    TeamLogoBadge,
  },
  data() {
    return {
      games: [],
      isLoading: true,
      now: Date.now(),
      timerId: null,
    };
  },
  computed: {
    displayGames() {
      return this.games
        .filter((game) => this.parseGameDate(game.date))
        .sort((a, b) => new Date(a.date) - new Date(b.date))
        .slice(0, 3);
    },
  },
  async mounted() {
    await this.fetchGames();
    this.emitDisplayedGames();
    this.timerId = window.setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeUnmount() {
    if (this.timerId) {
      window.clearInterval(this.timerId);
    }
  },
  methods: {
    async fetchGames() {
      try {
        const apiUrl = `${process.env.VUE_APP_API_URL}/football/fetchFeaturedGames`;
        const data = await fetchJsonWithCache({
          key: "football:featured",
          url: apiUrl,
          ttlMs: 20000,
        });
        this.games = data.map((game) => ({
          ...game,
          league: formatLeagueName(game.league),
        }));
      } catch (error) {
        console.error("Failed to fetch landing showcase games:", error);
        this.games = [];
      } finally {
        this.isLoading = false;
      }
    },
    emitDisplayedGames() {
      this.$emit(
        "showcase-games-loaded",
        this.displayGames.map((game) => game.id)
      );
    },
    isPrimaryGame(game) {
      return this.displayGames[1]?.id === game.id;
    },
    parseGameDate(value) {
      if (!value) {
        return null;
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }

      return parsed;
    },
    countdownFor(value) {
      const kickoff = this.parseGameDate(value);
      if (!kickoff) {
        return "Upcoming";
      }

      const diff = kickoff.getTime() - this.now;
      if (diff <= 0) {
        return "Starting";
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);

      if (days > 0) {
        return `${days}d ${hours}h`;
      }

      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }

      return `${minutes}m`;
    },
    kickoffLabel(value) {
      const kickoff = this.parseGameDate(value);
      if (!kickoff) {
        return "";
      }

      return kickoff.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    formattedOdds(value) {
      return Number(value).toFixed(2);
    },
    formattedProbability(value) {
      return `${(Number(value) * 100).toFixed(1)}%`;
    },
    openGame(game) {
      this.$router.push(`/football/${game.id}`);
    },
    goToFootball() {
      this.$router.push("/football");
    },
  },
};
</script>

<style scoped>
.landing-match-showcase {
  width: 100%;
  padding: 6px 16px 0;
  box-sizing: border-box;
}

.showcase-shell {
  width: min(1180px, 100%);
  margin: 0 auto;
}

.showcase-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
  align-items: stretch;
}

.showcase-card {
  padding: 18px;
  border: 1px solid #e2eaf2;
  border-radius: 28px;
  background:
    radial-gradient(circle at top right, rgba(240, 203, 109, 0.16), transparent 22%),
    radial-gradient(circle at bottom left, rgba(70, 133, 194, 0.12), transparent 24%),
    linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  box-shadow: 0 18px 40px rgba(21, 41, 68, 0.08);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.showcase-card-primary {
  transform: scale(1.03);
  border-color: #cfddea;
  box-shadow: 0 24px 50px rgba(21, 41, 68, 0.13);
}

.showcase-card-secondary {
  transform: scale(0.95);
}

.showcase-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 24px 44px rgba(21, 41, 68, 0.12);
  border-color: #d3dfe9;
}

.showcase-card-primary:hover {
  transform: translateY(-4px) scale(1.03);
}

.showcase-card-secondary:hover {
  transform: translateY(-4px) scale(0.95);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.league-pill,
.countdown-pill {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.league-pill {
  max-width: 68%;
  background: #eef4fa;
  color: #1d4f7b;
}

.countdown-pill {
  background: #173252;
  color: #ffffff;
}

.match-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 14px;
  margin-bottom: 20px;
}

.team-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #173252;
  font-weight: 700;
  text-align: center;
  line-height: 1.35;
}

.team-block span:last-child {
  min-height: 44px;
}

.versus-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.versus {
  color: #1f4e79;
  font-size: 1.2rem;
  font-weight: 700;
  text-transform: uppercase;
}

.kickoff {
  color: #748291;
  font-size: 0.8rem;
  white-space: nowrap;
}

.prediction-panel {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.prediction-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 74px;
  padding: 10px;
  border: 1px solid #d6ecde;
  border-radius: 18px;
  background: #edf8f1;
  text-align: center;
}

.metric-label {
  margin-bottom: 6px;
  color: #6b7a89;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.metric-value {
  color: #18324f;
  font-weight: 700;
  line-height: 1.3;
}

.showcase-actions {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}

.football-cta {
  padding: 14px 28px;
  border: 1px solid #16314d;
  border-radius: 999px;
  background: #16314d;
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.football-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 14px 28px rgba(22, 49, 77, 0.18);
  background: #21486e;
}

@media (max-width: 980px) {
  .showcase-grid {
    grid-template-columns: 1fr;
  }

  .showcase-card-primary,
  .showcase-card-secondary,
  .showcase-card-primary:hover,
  .showcase-card-secondary:hover {
    transform: none;
  }
}

@media (max-width: 640px) {
  .landing-match-showcase {
    padding: 2px 12px 0;
  }

  .showcase-card {
    padding: 16px;
    border-radius: 22px;
  }

  .match-row {
    gap: 10px;
  }

  .league-pill,
  .countdown-pill {
    font-size: 0.72rem;
  }
}
</style>
